import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Layout from "../components/layout/Layout";
import { useAuth } from "../context/AuthContext";
import PortalWideDefaultsPage from "./PortalWideDefaultsPage";
import AdminUserManagement from "./AdminUserManagement";
import SignaturesSettingsPage from "./SignaturesSettingsPage";
import TypesListsSettingsPage from "./TypesListsSettingsPage";
import ChartOfAccountsSettingsPage from "./ChartOfAccountsSettingsPage";
import BudgetLineItemsSettingsPage from "./BudgetLineItemsSettingsPage";
import DocumentTemplatesSettingsPage from "./DocumentTemplatesSettingsPage";
import { hasFinanceAccess, hasSystemAdminAccess } from "../utils/roleAccess";
import { useBreadcrumb } from "../context/BreadcrumbContext";

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ mt: 2 }}>{children}</Box>;
}

const TAB_KEYS = [
  "company",
  "types-lists",
  "chart-of-accounts",
  "budget-line-items",
  "document-templates",
  "signatures",
  "users",
];

export default function SettingsPage() {
  const { user, roleCapabilities } = useAuth();
  const { setCustomPath, clearCustomPath } = useBreadcrumb();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);

  const userRoles = user?.claims_role || user?.roles || [];
  const isAdmin = hasSystemAdminAccess(userRoles, roleCapabilities);
  const canAccessFinance = hasFinanceAccess(userRoles, roleCapabilities);

  const availableTabs = useMemo(() => {
    if (isAdmin) return TAB_KEYS;
    if (canAccessFinance) return ["company", "chart-of-accounts", "budget-line-items"];
    return TAB_KEYS.slice(0, 1);
  }, [isAdmin, canAccessFinance]);
  const searchParams = new URLSearchParams(location.search);
  const requestedTab = searchParams.get("tab");

  useEffect(() => {
    setCustomPath([{ label: "Settings", path: null }]);
    return () => clearCustomPath();
  }, [setCustomPath, clearCustomPath]);

  useEffect(() => {
    const tab = requestedTab === "finance" ? "chart-of-accounts" : requestedTab;
    const requestedIndex = availableTabs.indexOf(tab || "company");
    setActiveTab(requestedIndex >= 0 ? requestedIndex : 0);
  }, [availableTabs, requestedTab]);

  const navigateToTab = (nextTab) => {
    const nextTabKey = availableTabs[nextTab] || availableTabs[0];
    const nextParams = new URLSearchParams(location.search);
    if (nextTabKey === "company") {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", nextTabKey);
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextParams.toString() ? `?${nextParams.toString()}` : "",
      },
      { replace: false }
    );
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Settings
        </Typography>

        <Paper elevation={2} sx={{ p: 0, borderRadius: 2 }}>
          <Box sx={{ px: 2, pt: 1 }}>
            <Tabs
              value={activeTab}
              onChange={(event, nextTab) => navigateToTab(nextTab)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Company Information" />
              {isAdmin && <Tab label="Types & Lists" />}
              {canAccessFinance && <Tab label="Chart of Accounts" />}
              {canAccessFinance && <Tab label="Budget Line Items" />}
              {isAdmin && <Tab label="Document Templates" />}
              {isAdmin && <Tab label="Signatures" />}
              {isAdmin && <Tab label="User Management" />}
            </Tabs>
          </Box>
          <Box sx={{ p: 2 }}>
            <TabPanel value={activeTab} index={0}>
              <PortalWideDefaultsPage embedded showEstateCodeSettings={canAccessFinance} />
            </TabPanel>
            {isAdmin && (
              <TabPanel value={activeTab} index={availableTabs.indexOf("types-lists")}>
                <TypesListsSettingsPage />
              </TabPanel>
            )}
            {canAccessFinance && (
              <TabPanel value={activeTab} index={availableTabs.indexOf("chart-of-accounts")}>
                <ChartOfAccountsSettingsPage />
              </TabPanel>
            )}
            {canAccessFinance && (
              <TabPanel value={activeTab} index={availableTabs.indexOf("budget-line-items")}>
                <BudgetLineItemsSettingsPage />
              </TabPanel>
            )}
            {isAdmin && (
              <TabPanel value={activeTab} index={availableTabs.indexOf("document-templates")}>
                <DocumentTemplatesSettingsPage />
              </TabPanel>
            )}
            {isAdmin && (
              <TabPanel value={activeTab} index={availableTabs.indexOf("signatures")}>
                <SignaturesSettingsPage />
              </TabPanel>
            )}
            {isAdmin && (
              <TabPanel value={activeTab} index={availableTabs.indexOf("users")}>
                <AdminUserManagement embedded />
              </TabPanel>
            )}
          </Box>
        </Paper>
      </Box>
    </Layout>
  );
}
